/**
 * TanStack Query hooks for Platform Settings API
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "@/lib/api-client";
import type {
    BulkUpdateSettingsRequest,
    Setting,
    SettingCategory,
    SettingsResponse,
    SettingUpdateResponse,
    UpdateSettingRequest,
} from "@/types/api/settings";
import { SCHEDULER_QUERY_KEYS } from "./use-scheduler-jobs";

// ============================================================================
// Query Keys
// ============================================================================

export const SETTINGS_QUERY_KEYS = {
    all: ["admin-settings"] as const,
    lists: () => [...SETTINGS_QUERY_KEYS.all, "list"] as const,
    list: (scope?: string) => [...SETTINGS_QUERY_KEYS.lists(), scope] as const,
    category: (category: SettingCategory, scope?: string) => [...SETTINGS_QUERY_KEYS.all, "category", category, scope] as const,
    setting: (category: SettingCategory, key: string, scope?: string) => [...SETTINGS_QUERY_KEYS.all, "setting", category, key, scope] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all settings grouped by category
 * @param scope - 'global' or company_id for company-specific settings
 */
export function useSettings(scope: string = "global") {
    return useQuery({
        queryKey: SETTINGS_QUERY_KEYS.list(scope),
        queryFn: async (): Promise<SettingsResponse> => {
            const params = scope !== "global" ? { company_id: scope } : undefined;
            return await apiGet<SettingsResponse>("/admin/settings", { params });
        },
        staleTime: 30 * 1000, // 30 seconds
    });
}

/**
 * Fetch settings for a specific category
 */
export function useCategorySettings(category: SettingCategory, scope: string = "global") {
    return useQuery({
        queryKey: SETTINGS_QUERY_KEYS.category(category, scope),
        queryFn: async (): Promise<Setting[]> => {
            const params = scope !== "global" ? { company_id: scope } : undefined;
            return await apiGet<Setting[]>(`/admin/settings/${category}`, { params });
        },
        staleTime: 30 * 1000,
    });
}

/**
 * Fetch a single setting
 * Tries public endpoint first, falls back to admin endpoint if needed
 */
export function useSetting(category: SettingCategory, key: string, scope: string = "global") {
    return useQuery({
        queryKey: SETTINGS_QUERY_KEYS.setting(category, key, scope),
        queryFn: async (): Promise<Setting> => {
            const params = scope !== "global" ? { company_id: scope } : undefined;
            // Try public endpoint first (for settings like eve_care_chat.enable_attachments)
            try {
                return await apiGet<Setting>(`/admin/settings/public/${category}/${key}`, { params });
            } catch (error: any) {
                // If public endpoint returns 403 or 404, try admin endpoint
                if (error?.status === 403 || error?.status === 404) {
                    return await apiGet<Setting>(`/admin/settings/${category}/${key}`, { params });
                }
                throw error;
            }
        },
        enabled: !!category && !!key,
        staleTime: 30 * 1000,
        retry: false, // Don't retry on 403 errors
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update a single setting
 */
export function useUpdateSetting() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            category,
            key,
            value,
            companyId,
        }: {
            category: SettingCategory;
            key: string;
            value: unknown;
            companyId?: string;
        }): Promise<SettingUpdateResponse> => {
            const payload: UpdateSettingRequest = { value };
            if (companyId && companyId !== "global") {
                payload.company_id = companyId;
            }
            return await apiPut<SettingUpdateResponse>(`/admin/settings/${category}/${key}`, payload);
        },
        onSuccess: (_data, variables) => {
            // Invalidate all settings queries to ensure consistency
            queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SETTINGS_QUERY_KEYS.category(variables.category, variables.companyId),
            });
        },
    });
}

/**
 * Bulk update settings for a category
 */
export function useUpdateCategorySettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            category,
            settings,
            companyId,
        }: {
            category: SettingCategory;
            settings: Record<string, unknown>;
            companyId?: string;
        }): Promise<SettingUpdateResponse[]> => {
            const payload: BulkUpdateSettingsRequest = { settings };
            if (companyId && companyId !== "global") {
                payload.company_id = companyId;
            }
            return await apiPost<SettingUpdateResponse[]>(`/admin/settings/${category}/bulk`, payload);
        },
        onSuccess: (_data, variables) => {
            // Invalidate settings queries
            queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SETTINGS_QUERY_KEYS.category(variables.category, variables.companyId),
            });
            // When scheduler settings change, also refresh the scheduler jobs panel
            if (variables.category === "scheduler") {
                queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            }
        },
    });
}

/**
 * Reset a setting to its default value
 */
export function useResetSetting() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            category,
            key,
            companyId,
        }: {
            category: SettingCategory;
            key: string;
            companyId?: string;
        }): Promise<SettingUpdateResponse> => {
            const params = companyId && companyId !== "global" ? { company_id: companyId } : undefined;
            return await apiPost<SettingUpdateResponse>(`/admin/settings/${category}/${key}/reset`, undefined, { params });
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SETTINGS_QUERY_KEYS.category(variables.category, variables.companyId),
            });
        },
    });
}

/**
 * Reset all settings in a category to defaults
 */
export function useResetCategorySettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            category,
            companyId,
        }: {
            category: SettingCategory;
            companyId?: string;
        }): Promise<SettingUpdateResponse[]> => {
            const params = companyId && companyId !== "global" ? { company_id: companyId } : undefined;
            return await apiPost<SettingUpdateResponse[]>(`/admin/settings/${category}/reset`, undefined, { params });
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({
                queryKey: SETTINGS_QUERY_KEYS.category(variables.category, variables.companyId),
            });
            // When scheduler settings are reset, also refresh the scheduler jobs panel
            if (variables.category === "scheduler") {
                queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            }
        },
    });
}

/**
 * Validate a setting value before saving
 */
export function useValidateSetting() {
    return useMutation({
        mutationFn: async ({
            category,
            key,
            value,
        }: {
            category: SettingCategory;
            key: string;
            value: unknown;
        }): Promise<{ valid: boolean; errors?: string[] }> => {
            return await apiPost<{ valid: boolean; errors?: string[] }>("/admin/settings/validate", {
                category,
                key,
                value,
            });
        },
    });
}
